/* eslint-disable */
import type { RequestContext } from "../RequestContextProto.pb";
import type { ResponseContext } from "../ResponseContextProto.pb";
import type { CustomerVerificationData } from "../common/customer_verification.pb";
import type { DeviceLocationHeuristics } from "../common/device_location_heuristics.pb";
import type { SignInType } from "../common/sign_in_type.pb";
import type { SmsDeliveryMechanism } from "../common/sms_delivery_mechanism.pb";
import type { ClientSecurityContext } from "./initiateSession.pb";

/** The request to register an SMS number as the first step to authenticating it. */
export interface RegisterSmsRequest {
  request_context?: RequestContext;
  sms_number?: string;
  verification_mechanism?: SmsDeliveryMechanism;
  /**
   * Deprecated. Use client scenario header.
   *
   * @deprecated
   */
  scenario?: RegisterSmsRequestScenario;
  /** True if the phone number matches the phone number pre-filled using phone's information. */
  pre_filled_from_phone?: boolean;
  /** The payments being acted upon. */
  payment_tokens?: string[];
  /** If true, this request will fail if a new customer must be created for this alias to be claimed. */
  existing_customer_only?: boolean;
  device_location_heuristics?: DeviceLocationHeuristics;
  /** A client generated token that triggers a callback to the app when included in the SMS message. */
  app_callback_token?: string;
  /** Tags are out of order. Next tag = 13. */
  account_id?: string;
}

export enum RegisterSmsRequestScenario {
  /** ONBOARDING - The customer is registering their SMS as part of onboarding. */
  ONBOARDING = "ONBOARDING",
  /** PROFILE - The customer is registering their SMS from their profile. */
  PROFILE = "PROFILE",
}

export interface RegisterSmsResponse {
  response_context?: ResponseContext;
  status?: RegisterSmsResponseStatus;
}

export enum RegisterSmsResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - The request was successful, an SMS has been sent. */
  SUCCESS = "SUCCESS",
  /**
   * INVALID_SMS_NUMBER - The SMS number provided did not conform to an acceptable format. The number must be a
   * 10-digit number (or 11 digits and start with a 1). All non-digit characters will be ignored.
   */
  INVALID_SMS_NUMBER = "INVALID_SMS_NUMBER",
  /**
   * TOO_MANY_REQUESTS - There have been too many requests to register this SMS number in the last 24 hours. The user
   * can try again later.
   */
  TOO_MANY_REQUESTS = "TOO_MANY_REQUESTS",
  /**
   * DUPLICATE_SMS_NUMBER - The sms number is already associated with the requesting user.
   * Added for 2.11
   */
  DUPLICATE_SMS_NUMBER = "DUPLICATE_SMS_NUMBER",
  /** CUSTOMER_DOES_NOT_EXIST - No customer exists for this SMS number, and existing_customer_only was set in the request. */
  CUSTOMER_DOES_NOT_EXIST = "CUSTOMER_DOES_NOT_EXIST",
}

/** The request to verify an SMS number with a verification code. */
export interface VerifySmsRequest {
  request_context?: RequestContext;
  sms_number?: string;
  sms_verification_code?: string;
  /** The payments being acted upon. */
  payment_tokens?: string[];
  account_id?: string;
  /**
   * The clients security / privacy context information. This data is currently used in client
   * app-level encryption and hardware attestation. Provide this when session is to be minted
   * during verifySms.
   */
  client_security_context?: ClientSecurityContext;
}

/**
 * The response to a request to verify an SMS number.  It contains the session token used in other
 * app requests.
 */
export interface VerifySmsResponse {
  response_context?: ResponseContext;
  status?: VerifySmsResponseStatus;
  /** @deprecated */
  session_token?: string;
  /**
   * No longer used.
   *
   * @deprecated
   */
  customer_verification_data?: CustomerVerificationData;
  /** Tags are out of order. Next tag = 14 */
  sign_in_type?: SignInType;
}

export enum VerifySmsResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - The request was successful. A session token has been provided. */
  SUCCESS = "SUCCESS",
  /** INVALID_SMS_NUMBER - The SMS number provided did not conform to an acceptable format or has not been registered. */
  INVALID_SMS_NUMBER = "INVALID_SMS_NUMBER",
  /** INVALID_VERIFICATION_CODE - The verification code is invalid. The user should retry with the correct verification code. */
  INVALID_VERIFICATION_CODE = "INVALID_VERIFICATION_CODE",
  /**
   * EXPIRED_VERIFICATION_CODE - The verification code has expired. The user should register their SMS again to get a new
   * verification code.
   */
  EXPIRED_VERIFICATION_CODE = "EXPIRED_VERIFICATION_CODE",
  /**
   * TOO_MANY_FAILED_ATTEMPTS - There have been too many failed attempts to verify this SMS number in the past 7 days.
   * The user can try again later.
   */
  TOO_MANY_FAILED_ATTEMPTS = "TOO_MANY_FAILED_ATTEMPTS",
  /** SUCCESS_WITH_VERIFICATION_DATA - No longer used. */
  SUCCESS_WITH_VERIFICATION_DATA = "SUCCESS_WITH_VERIFICATION_DATA",
  /** NOT_ELIGIBLE - Only for 2.5+ apps: this action was related to a customer merge that was deemed not eligible. */
  NOT_ELIGIBLE = "NOT_ELIGIBLE",
}
